import type { Point2d } from ".."
import { wrapAround } from "../../utils/wrapAround"

const wrapRad = (rad: number) => wrapAround(rad, [0, Math.PI * 2])

export interface RightAnglePoints {
  p1: Point2d
  p2: Point2d
  p3: Point2d
}

const drawRightAngle = (
  angleToSnapTo: number,
  lastAngle: number,
  nodeWeDrawFrom: Point2d,
): RightAnglePoints | null => {
  const isRightAngle =
    angleToSnapTo === wrapRad(Math.PI / 2 + lastAngle) ||
    angleToSnapTo === wrapRad(Math.PI * 1.5 + lastAngle)

  if (isRightAngle) {
    const dir = lastAngle > Math.PI ? -1 : 1

    const d = 15
    const pointOnLastEdge = {
      x: nodeWeDrawFrom.x + Math.cos(lastAngle) * (d * dir),
      y: nodeWeDrawFrom.y + Math.sin(lastAngle) * (d * dir),
    }

    return {
      p1: {
        x: nodeWeDrawFrom.x + Math.cos(angleToSnapTo) * d,
        y: nodeWeDrawFrom.y + Math.sin(angleToSnapTo) * d,
      },
      p2: pointOnLastEdge,
      p3: {
        x: pointOnLastEdge.x + Math.cos(angleToSnapTo) * d,
        y: pointOnLastEdge.y + Math.sin(angleToSnapTo) * d,
      },
    }
  }

  return null
}

export const findClosestLineSnapPoint = (
  p1: Point2d,
  p2: Point2d,
  lastAngle: number,
  slices = 36,
): {
  snapPoint: Point2d
  rightAnglePoints: RightAnglePoints | null
} => {
  // p1 is the mouse position
  // p2 is the lastPoint position
  // lastAngle is angleOfLastEdge
  const dx = p1.x - p2.x
  const dy = p1.y - p2.y
  const distance = Math.sqrt(dx * dx + dy * dy)
  const angleToNewPoint = Math.atan2(p1.y - p2.y, p1.x - p2.x)
  const sliceAngle = Math.PI / slices
  const angleChange = wrapRad(wrapRad(angleToNewPoint) - wrapRad(lastAngle))
  const angleToSnapTo = wrapRad(
    Math.round(angleChange / sliceAngle) * sliceAngle + lastAngle,
  )

  const rightAnglePoints = drawRightAngle(angleToSnapTo, lastAngle, p2)

  const snapPoint = {
    x: p2.x + Math.cos(angleToSnapTo) * distance,
    y: p2.y + Math.sin(angleToSnapTo) * distance,
  }

  return {
    snapPoint,
    rightAnglePoints,
  }
}
